import React, { useState, useEffect, useRef } from 'react';
import daveWave from '../assets/images/dave_wave.svg';
import { Loader } from '@googlemaps/js-api-loader';

/**
 * Pin Google Places' `.pac-container` dropdown to the input element using
 * `position: fixed` and viewport-relative coordinates.
 *
 * Google's legacy Autocomplete appends `.pac-container` to `document.body`
 * with `position: absolute` and a `top` value computed from the input's
 * page coordinates. Inside an auto-resizing iframe (or any layout that
 * shifts after first paint), that calculation drifts and the dropdown ends
 * up far away from the input — typically at the bottom of the page.
 *
 * This helper watches for the container to appear, then forces it to track
 * the input's bounding rect on every scroll/resize/input event. Returns a
 * cleanup function that disconnects observers and listeners.
 */
function pinPacContainer(input: HTMLInputElement): () => void {
  let pac: HTMLElement | null = null;
  let rafId: number | null = null;

  const apply = () => {
    if (!pac || !input.isConnected) return;
    const rect = input.getBoundingClientRect();
    pac.style.position = 'fixed';
    pac.style.top = `${rect.bottom + 4}px`;
    pac.style.left = `${rect.left}px`;
    pac.style.width = `${rect.width}px`;
    pac.style.zIndex = '10000';
  };

  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(apply);
  };

  const findPac = () => {
    // Multiple containers can exist (one per Autocomplete instance). Prefer
    // the most recently attached one — that's what's currently being shown.
    const list = document.querySelectorAll<HTMLElement>('.pac-container');
    if (list.length === 0) return;
    const next = list[list.length - 1];
    if (next === pac) return;
    pac = next;
    apply();
  };

  // Container is created lazily on first focus/keystroke — observe body.
  const observer = new MutationObserver(findPac);
  observer.observe(document.body, { childList: true });
  findPac();

  // Capture-phase scroll so we catch any scrollable ancestor too.
  window.addEventListener('scroll', schedule, true);
  window.addEventListener('resize', schedule);
  input.addEventListener('input', schedule);
  input.addEventListener('focus', schedule);

  return () => {
    observer.disconnect();
    window.removeEventListener('scroll', schedule, true);
    window.removeEventListener('resize', schedule);
    input.removeEventListener('input', schedule);
    input.removeEventListener('focus', schedule);
    if (rafId !== null) cancelAnimationFrame(rafId);
  };
}

interface AddressStepProps {
  onSubmit: (address: string, placeId?: string, email?: string) => void;
  loading: boolean;
  initialValue: string;
  /** When set from URL/embed, passed to submit if Autocomplete has no place yet */
  prefilledPlaceId?: string;
  /** Pre-filled by the embedded address widget via the `email` query param */
  initialEmail?: string;
  customerType: 'residential' | 'business' | null;
  onCustomerTypeChange: (type: 'residential' | 'business') => void;
}

// Loose on purpose — the Laravel FormRequest is the authority. This only stops
// obvious typos before we spend a qualification call on them.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddressStep: React.FC<AddressStepProps> = ({ 
  onSubmit, 
  loading, 
  initialValue,
  prefilledPlaceId = '',
  initialEmail = '',
  customerType,
  onCustomerTypeChange 
}) => {
  const [address, setAddress] = useState(initialValue);
  const [email, setEmail] = useState(initialEmail);
  const [emailTouched, setEmailTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const attachOnceRef = useRef<Promise<void> | null>(null);
  const pacCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setAddress(initialValue);
  }, [initialValue]);

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const emailValid = EMAIL_PATTERN.test(email.trim());
  const showEmailError = emailTouched && email.trim().length > 0 && !emailValid;

  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      if (pacCleanupRef.current) {
        pacCleanupRef.current();
        pacCleanupRef.current = null;
      }
      attachOnceRef.current = null;
    };
  }, []);

  const attachAutocomplete = () => {
    if (autocompleteRef.current || !inputRef.current) return;

    if (!attachOnceRef.current) {
      attachOnceRef.current = (async () => {
        try {
          const loader = new Loader({
            apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
            version: 'weekly',
            libraries: ['places'],
          });
          await loader.load();
          if (autocompleteRef.current || !inputRef.current || !document.contains(inputRef.current)) return;
          autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: 'au' },
            fields: ['formatted_address', 'place_id', 'address_components', 'geometry'],
            bounds: new google.maps.LatLngBounds(
              { lat: -35.5, lng: 112.5 },  // SW corner of WA
              { lat: -13.5, lng: 129.0 }   // NE corner of WA
            ),
          });

          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current?.getPlace();
            if (place && place.formatted_address) {
              setAddress(place.formatted_address);
            }
          });

          // Pin Google's `.pac-container` dropdown directly under the input —
          // the default page-coordinate calculation drifts in our auto-resizing
          // iframe and ends up at the bottom of the page.
          if (!pacCleanupRef.current) {
            pacCleanupRef.current = pinPacContainer(inputRef.current);
          }
        } catch (error) {
          console.error('Error loading or initializing Google Places Autocomplete:', error);
        }
      })();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim() || !customerType) return;
    // Email is mandatory: the qualification is recorded against it.
    if (!emailValid) {
      setEmailTouched(true);
      return;
    }
    const place = autocompleteRef.current?.getPlace();
    const finalAddress = place?.formatted_address || address;
    const placeId = place?.place_id || prefilledPlaceId || undefined;
    onSubmit(finalAddress, placeId, email.trim());
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    if (!customerType) {
      e.preventDefault();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-crisp-orange">
      {/* Header with Dave */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-crisp-dark-blue mb-2">Let's Get Started!</h2>
          <p className="text-crisp-grey">Please select residential or business to get started.</p>
        </div>
        <img src={daveWave} alt="Dave waving" className="w-32 h-32 ml-4 flex-shrink-0" />
      </div>
      
      {/* Customer Type Selection */}
      <div className="mb-8">
        
        <div className="grid md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onCustomerTypeChange('residential')}
            className={`p-6 rounded-lg border-2 transition-all ${
              customerType === 'residential'
                ? 'border-crisp-orange bg-crisp-orange bg-opacity-10 shadow-lg'
                : 'border-gray-300 hover:border-crisp-orange hover:shadow-md'
            }`}
          >
            <div className="flex items-center mb-3">
              <svg className="w-8 h-8 text-crisp-orange mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Residential</h3>
            </div>
            <p className="text-sm text-gray-600 text-left">Fast, reliable internet for your home</p>
          </button>

          <button
            type="button"
            onClick={() => onCustomerTypeChange('business')}
            className={`p-6 rounded-lg border-2 transition-all ${
              customerType === 'business'
                ? 'border-crisp-orange bg-crisp-orange bg-opacity-10 shadow-lg'
                : 'border-gray-300 hover:border-crisp-orange hover:shadow-md'
            }`}
          >
            <div className="flex items-center mb-3">
              <svg className="w-8 h-8 text-crisp-orange mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900">Business</h3>
            </div>
            <p className="text-sm text-gray-600 text-left">Professional connectivity for your business</p>
          </button>
        </div>
      </div>

      {/* Address Entry */}
      <div className="transition-all">
        <h2 className="text-2xl font-bold text-crisp-orange mb-4">Enter Your Address</h2>
        <p className="text-gray-700 mb-6">
          {"Let's check if we can provide internet service at your location."}
        </p>

        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
        <div className="mb-6">
          <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            ref={inputRef}
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onFocus={attachAutocomplete}
            placeholder="Start typing your address..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-crisp-orange focus:border-crisp-orange transition-colors"
            required
            disabled={loading}
            aria-label="Street Address"
            aria-required="true"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            placeholder="you@example.com"
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-crisp-orange focus:border-crisp-orange transition-colors ${
              showEmailError ? 'border-red-500' : 'border-gray-300'
            }`}
            required
            autoComplete="email"
            disabled={loading}
            aria-label="Email Address"
            aria-required="true"
            aria-invalid={showEmailError}
          />
          {showEmailError ? (
            <p className="mt-2 text-sm text-red-600" role="alert">
              Please enter a valid email address.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-500">
              {"We'll send your coverage result and quote here."}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !address.trim() || !emailValid || !customerType}
          className="w-full bg-crisp-orange text-white font-bold py-3 px-6 rounded-lg hover:bg-opacity-90 hover:shadow-lg transition-all disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
          aria-busy={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking...
            </span>
          ) : 'Check Availability'}
        </button>
      </form>
      </div>
    </div>
  );
};

export default AddressStep;
